import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, FileSpreadsheet, Trash2, Undo2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Combobox } from '@/shared/components/ui/combobox';
import { listActiveCuentasContables } from '@/shared/api/controllers/cuentasContables';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { cn } from '@/shared/lib/cn';
import { reclasificarCuentaContable } from '@/features/tesoreria/api';
import { type ReclasImportCambio, type ReclasImportResponse } from './api';

/**
 * Reimportar un Excel exportado para corregir la imputación en masa.
 *
 * Lo usan los dos reportes que exportan movimientos: Cierres de Caja (un
 * cierre) e Histórico de Movimientos (el libro entero). Por eso no sabe de
 * dónde salió el archivo — recibe `subirArchivo`, que ya apunta al endpoint que
 * corresponde.
 *
 * Flujo en dos pasos a propósito: subir NO escribe. El server devuelve qué
 * cambiaría y recién con "Aplicar" se tocan los movimientos, uno por uno,
 * contra el endpoint de reimputación de Tesorería — así cada cambio pasa por
 * las mismas validaciones que el diálogo manual y queda registrado en
 * `cash_movement_reclass_log` con su usuario.
 *
 * Las filas que no casan con ningún movimiento se descartan y sólo se informa
 * cuántas fueron: un Excel al que le agregaron filas a mano no debe poder crear
 * ni tocar nada.
 */
type OpcionCuenta = { key: string; cuenta: string; categoria: string };

/**
 * El selector muestra todo el plan de cuentas, pero lo que el server detectó
 * como parecido a lo tipeado va arriba de todo — con 53 cuentas activas,
 * hacer scroll hasta la correcta es el 90% del trabajo.
 */
function ordenarSugeridasPrimero(
  catalogo: OpcionCuenta[],
  sugeridas: Array<{ cuenta: string; categoria: string }> | undefined,
): OpcionCuenta[] {
  if (!sugeridas || sugeridas.length === 0) return catalogo;
  const claves = new Set(sugeridas.map((o) => `${o.categoria}||${o.cuenta}`));
  const arriba = catalogo.filter((o) => claves.has(o.key));
  const resto = catalogo.filter((o) => !claves.has(o.key));
  return [...arriba, ...resto];
}

/**
 * ¿Esta fila se puede arreglar acá mismo?
 *
 * Sólo el par inexistente o incompleto: son errores de tipeo en el Excel. Una
 * fila anulada, estructural o desalineada la va a rechazar el server pase lo
 * que pase, así que ofrecerle un selector es mandar al usuario a un camino sin
 * salida — elegía una cuenta, la fila se ponía verde, el botón se destrababa y
 * recién al aplicar aparecía el error. Para esas, la única salida es descartar.
 */
function esCorregible(c: ReclasImportCambio): boolean {
  return c.problemaTipo === 'par_invalido' || c.problemaTipo === 'incompleto';
}

export function ImportarReclasificacionDialog({
  contexto,
  origen,
  subirArchivo,
  onClose,
  onAplicado,
}: {
  /** Qué se está importando, para el título. Ej: "MercadoPago · 30/09/2025". */
  contexto: string;
  /**
   * De qué reporte salió el archivo, para el `motivo` del asiento en
   * `cash_movement_reclass_log`. Es el rastro con el que después se explica
   * quién cambió qué: decía "del cierre" para todo, así que una corrección
   * hecha desde el Histórico quedaba registrada como si fuera de un cierre.
   */
  origen: string;
  /** Sube el archivo al endpoint del reporte y devuelve la vista previa. */
  subirArchivo: (file: File) => Promise<ReclasImportResponse>;
  onClose: () => void;
  /** Se dispara si se aplicó al menos un cambio. */
  onAplicado?: () => void;
}) {
  const [preview, setPreview] = useState<ReclasImportResponse | null>(null);
  const [progreso, setProgreso] = useState<{ hechos: number; total: number } | null>(null);
  // Pares que el usuario corrigió acá mismo, por id de movimiento. Sólo para
  // filas cuyo par no existía: el Excel decía algo que el plan de cuentas no
  // tiene, y en vez de mandarlo de vuelta a corregir la planilla lo resuelve
  // en pantalla.
  const [corregidos, setCorregidos] = useState<
    Record<number, { cuenta: string; categoria: string }>
  >({});
  const [arrastrando, setArrastrando] = useState(false);
  // Filas que el usuario decide NO aplicar. Quedan a la vista tachadas: se
  // descartan a propósito, no se esconden.
  const [descartados, setDescartados] = useState<Record<number, true>>({});
  const { config: activeCountry } = useActiveCountry();

  // Catálogo completo. Las sugerencias del server sirven cuando el usuario
  // tipeó algo parecido a una cuenta real, pero si escribió cualquier cosa
  // ("Tests / Test") no hay ninguna y se quedaba sin salida — así que el
  // selector ofrece SIEMPRE todo el plan de cuentas.
  const cuentasQ = useQuery({
    queryKey: ['cuentas-contables', activeCountry.code, 'activas'],
    queryFn: listActiveCuentasContables,
    staleTime: 5 * 60_000,
  });
  const opcionesCatalogo = useMemo(() => {
    const out: Array<{ key: string; cuenta: string; categoria: string }> = [];
    for (const c of cuentasQ.data ?? []) {
      const cuenta = (c.fields.CuentaContable_CC ?? '').trim();
      const categoria = (c.fields.Categoria_CC ?? '').trim();
      if (!cuenta || !categoria) continue;
      out.push({ key: `${categoria}||${cuenta}`, cuenta, categoria });
    }
    return out.sort(
      (a, b) =>
        a.cuenta.localeCompare(b.cuenta, 'es') ||
        a.categoria.localeCompare(b.categoria, 'es'),
    );
  }, [cuentasQ.data]);

  /** Valida acá lo obvio (que sea .xlsx) para no gastar un viaje al server
   *  cuando alguien suelta un PDF sobre el recuadro. */
  const tomarArchivo = (f: File | undefined | null) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('El archivo tiene que ser .xlsx', {
        description: `Soltaste "${f.name}".`,
      });
      return;
    }
    subir.mutate(f);
  };

  const subir = useMutation({
    mutationFn: (file: File) => subirArchivo(file),
    onSuccess: (r) => {
      setPreview(r);
      setCorregidos({});
      setDescartados({});
    },
    onError: (err) =>
      toast.error('No se pudo leer el archivo', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });

  /** El par que finalmente se va a mandar: el del Excel o el corregido acá. */
  const parFinal = (c: ReclasImportCambio) =>
    corregidos[c.id] ?? { cuenta: c.cuentaNueva, categoria: c.categoriaNueva };
  const aplicables: ReclasImportCambio[] =
    preview?.outcome === 'ok'
      ? preview.cambios.filter(
          (c) =>
            !descartados[c.id] &&
            (!c.problema || (corregidos[c.id] && esCorregible(c))),
        )
      : [];
  /** Filas rotas que el usuario todavía no resolvió ni descartó. */
  const pendientes: ReclasImportCambio[] =
    preview?.outcome === 'ok'
      ? preview.cambios.filter((c) => c.problema && !corregidos[c.id] && !descartados[c.id])
      : [];

  const aplicar = useMutation({
    mutationFn: async () => {
      let ok = 0;
      const fallidos: string[] = [];
      for (const [i, c] of aplicables.entries()) {
        setProgreso({ hechos: i, total: aplicables.length });
        try {
          const par = parFinal(c);
          const r = await reclasificarCuentaContable(c.id, {
            categoria: par.categoria,
            cuentaContable: par.cuenta,
            motivo: `Importación de Excel · ${origen}${
              corregidos[c.id] ? ' (par corregido en pantalla)' : ''
            }`,
          });
          if (r.outcome === 'updated' || r.outcome === 'sin_cambios') ok += 1;
          else fallidos.push(`${c.id}: ${r.message}`);
        } catch (err) {
          fallidos.push(`${c.id}: ${err instanceof Error ? err.message : 'error'}`);
        }
      }
      setProgreso({ hechos: aplicables.length, total: aplicables.length });
      return { ok, fallidos };
    },
    onSuccess: ({ ok, fallidos }) => {
      if (fallidos.length === 0) {
        toast.success(`${ok} movimiento${ok === 1 ? '' : 's'} reimputado${ok === 1 ? '' : 's'}`);
      } else {
        toast.warning(`${ok} aplicados, ${fallidos.length} con error`, {
          description: fallidos.slice(0, 3).join(' · '),
          duration: 10000,
        });
      }
      if (ok > 0) onAplicado?.();
      onClose();
    },
    onError: (err) =>
      toast.error('Falló la importación', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });

  const procesando = subir.isPending || aplicar.isPending;

  return (
    <Dialog open onOpenChange={(o) => !o && !procesando && onClose()}>
      <DialogContent
        className="max-w-5xl"
        processing={
          (subir.isPending && 'Leyendo el archivo…') ||
          (aplicar.isPending &&
            `Aplicando ${progreso ? `${progreso.hechos + 1} de ${progreso.total}` : ''}…`)
        }
      >
        <DialogHeader>
          <DialogTitle>Importar Excel · {contexto}</DialogTitle>
          <DialogDescription>
            Subí el mismo archivo que exportaste, con las columnas{' '}
            <strong>Cuenta contable</strong> y <strong>Categoría</strong> corregidas. Las
            demás columnas no se leen.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {!preview && (
            <label
              className={cn(
                'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-10 text-center transition-colors',
                arrastrando
                  ? 'border-foreground bg-foreground/5'
                  : 'border-border hover:border-foreground/40 hover:bg-muted/40',
                procesando && 'pointer-events-none opacity-60',
              )}
              // Mismo gesto que el FileUploader del resto de la app: hay que
              // cancelar el dragover, si no el browser abre el archivo.
              onDragOver={(e) => {
                e.preventDefault();
                setArrastrando(true);
              }}
              onDragLeave={() => setArrastrando(false)}
              onDrop={(e) => {
                e.preventDefault();
                setArrastrando(false);
                tomarArchivo(e.dataTransfer?.files?.[0]);
              }}
            >
              <FileSpreadsheet className="h-8 w-8 text-muted-foreground" aria-hidden />
              <span className="text-sm font-medium text-foreground">
                {arrastrando ? 'Soltá el archivo acá' : 'Arrastrá el .xlsx o hacé clic para elegirlo'}
              </span>
              <span className="text-xs text-muted-foreground">
                Tiene que ser el exportado desde este cierre, sin mover columnas.
              </span>
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={(e) => {
                  tomarArchivo(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
          )}

          {preview?.outcome === 'layout_invalido' && (
            <LayoutInvalido
              message={preview.message}
              esperado={preview.esperado}
              recibido={preview.recibido}
              onReintentar={() => setPreview(null)}
            />
          )}

          {preview?.outcome === 'archivo_invalido' && (
            <div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-3 text-sm text-rose-900">
              <p className="font-semibold">No se pudo leer el archivo</p>
              <p className="mt-1 text-xs">{preview.message}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => setPreview(null)}
              >
                Elegir otro archivo
              </Button>
            </div>
          )}

          {preview?.outcome === 'ok' && (
            <>
              {/* Contadores VIVOS: al corregir un par en pantalla la fila
                  pasa de "con problema" a "a aplicar", y los chips tienen que
                  seguirla — si no, el botón dice 5 y el resumen dice 3. */}
              <Resumen
                resumen={{
                  ...preview.resumen,
                  conCambios: aplicables.length,
                  conProblema: pendientes.length,
                }}
                descartadas={Object.keys(descartados).length}
              />
              {pendientes.length > 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <span>
                    {(() => {
                      const corregibles = pendientes.filter(esCorregible).length;
                      const noCorregibles = pendientes.length - corregibles;
                      const partes: string[] = [];
                      if (corregibles > 0) {
                        partes.push(
                          `${corregibles} con una imputación que no existe en el plan de cuentas (elegí una válida o descartala)`,
                        );
                      }
                      if (noCorregibles > 0) {
                        partes.push(
                          `${noCorregibles} que no se pueden reimputar (descartalas para continuar)`,
                        );
                      }
                      return `Hay ${partes.join(' y ')}.`;
                    })()}
                  </span>
                </div>
              )}
              {preview.cambios.length === 0 ? (
                <div className="rounded-lg border bg-secondary px-3 py-6 text-center text-sm text-muted-foreground">
                  El archivo no trae ningún cambio de imputación.
                </div>
              ) : (
                <div className="max-h-[26rem] overflow-y-auto rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 font-medium">Movimiento</th>
                        <th className="px-3 py-2 font-medium">Cambio</th>
                        {/* El ancho sobra: el selector y el descartar van al
                            costado en vez de apilarse bajo el cambio. */}
                        <th className="w-[22rem] px-3 py-2 text-right font-medium">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {preview.cambios.map((c) => (
                        <tr
                          key={c.id}
                          className={cn(
                            c.problema &&
                              !corregidos[c.id] &&
                              !descartados[c.id] &&
                              'bg-rose-50/60',
                            corregidos[c.id] && 'bg-emerald-50/60',
                            descartados[c.id] && 'opacity-50',
                          )}
                        >
                          <td className="max-w-[28rem] px-3 py-2">
                            <div className="truncate text-foreground" title={c.descripcion}>
                              {c.descripcion || `#${c.id}`}
                            </div>
                            <div className="text-[11px] text-muted-foreground">#{c.id}</div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap items-center gap-1.5 text-xs">
                              <span className="text-muted-foreground line-through">
                                {c.cuentaActual || '—'} · {c.categoriaActual || '—'}
                              </span>
                              <ArrowRight
                                className="h-3 w-3 shrink-0 text-muted-foreground"
                                aria-hidden
                              />
                              <span
                                className={cn(
                                  'font-medium',
                                  corregidos[c.id] ? 'text-emerald-700' : 'text-foreground',
                                )}
                              >
                                {parFinal(c).cuenta || '—'} · {parFinal(c).categoria || '—'}
                              </span>
                            </div>
                            {c.problema && !corregidos[c.id] && (
                              <div className="mt-1 flex items-start gap-1 text-[11px] text-rose-700">
                                <AlertTriangle className="mt-px h-3 w-3 shrink-0" aria-hidden />
                                <span>{c.problema}</span>
                              </div>
                            )}
                            {descartados[c.id] && (
                              <div className="mt-1 text-[11px] text-muted-foreground">
                                Descartada — no se va a aplicar.
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 align-top">
                            <div className="flex items-center justify-end gap-2">
                              {/* Recategorizar en pantalla. El selector ofrece
                                  TODO el plan de cuentas: las sugerencias del
                                  server sólo existen cuando lo tipeado se
                                  parece a una cuenta real, y con un valor
                                  inventado ("Tests / Test") el usuario quedaba
                                  sin salida. Las sugeridas van primero igual. */}
                              {c.problema &&
                                esCorregible(c) &&
                                !corregidos[c.id] &&
                                !descartados[c.id] && (
                                <div className="min-w-0 flex-1">
                                  <Combobox
                                    value={null}
                                    onChange={(o) => {
                                      if (o) {
                                        setCorregidos((prev) => ({
                                          ...prev,
                                          [c.id]: { cuenta: o.cuenta, categoria: o.categoria },
                                        }));
                                      }
                                    }}
                                    items={ordenarSugeridasPrimero(opcionesCatalogo, c.opciones)}
                                    getKey={(o) => o.key}
                                    getLabel={(o) => o.cuenta}
                                    getSublabel={(o) => o.categoria}
                                    matcher={(o, q) =>
                                      o.cuenta.toLowerCase().includes(q) ||
                                      o.categoria.toLowerCase().includes(q)
                                    }
                                    placeholder={
                                      cuentasQ.isPending ? 'Cargando…' : 'Elegir imputación válida…'
                                    }
                                    disabled={procesando || cuentasQ.isPending}
                                    clearable={false}
                                  />
                                </div>
                              )}
                              {corregidos[c.id] && !descartados[c.id] && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setCorregidos((prev) => {
                                      const next = { ...prev };
                                      delete next[c.id];
                                      return next;
                                    })
                                  }
                                  disabled={procesando}
                                  title="Deshacer la corrección"
                                  aria-label="Deshacer la corrección"
                                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-card text-muted-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                  <Undo2 className="h-4 w-4" aria-hidden />
                                </button>
                              )}
                              {/* Descartar vale para CUALQUIER fila, no sólo
                                  las rotas: el usuario puede no querer aplicar
                                  un cambio perfectamente válido del Excel. */}
                              {descartados[c.id] ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setDescartados((prev) => {
                                      const next = { ...prev };
                                      delete next[c.id];
                                      return next;
                                    })
                                  }
                                  disabled={procesando}
                                  title="Volver a incluir esta fila"
                                  aria-label="Volver a incluir esta fila"
                                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-card text-muted-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                  <Undo2 className="h-4 w-4" aria-hidden />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setDescartados((prev) => ({ ...prev, [c.id]: true }))
                                  }
                                  disabled={procesando}
                                  title="Descartar: no aplicar este cambio"
                                  aria-label="Descartar este cambio"
                                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive transition-colors hover:bg-destructive/20 disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                  <Trash2 className="h-4 w-4" aria-hidden />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={procesando}>
            {preview?.outcome === 'ok' && aplicables.length > 0 ? 'Cancelar' : 'Cerrar'}
          </Button>
          {preview?.outcome === 'ok' && (aplicables.length > 0 || pendientes.length > 0) && (
            <Button
              type="button"
              onClick={() => aplicar.mutate()}
              // Nada se aplica mientras quede una fila rota sin resolver. Antes
              // el botón aplicaba las buenas y se saltaba las malas en silencio:
              // el usuario se iba convencido de que entró todo. Ahora hay que
              // corregir el par o descartar la fila explícitamente.
              disabled={procesando || pendientes.length > 0 || aplicables.length === 0}
              title={
                pendientes.length > 0
                  ? `Resolvé o descartá ${pendientes.length} fila${
                      pendientes.length === 1 ? '' : 's'
                    } con imputación inválida`
                  : undefined
              }
              className="gap-2"
            >
              <Upload className="h-4 w-4" aria-hidden />
              {pendientes.length > 0
                ? `${pendientes.length} sin resolver`
                : aplicables.length === 0
                  ? 'Nada para aplicar'
                  : `Aplicar ${aplicables.length} cambio${aplicables.length === 1 ? '' : 's'}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** El layout es un contrato: si no coincide, mostramos exactamente qué llegó. */
function LayoutInvalido({
  message,
  esperado,
  recibido,
  onReintentar,
}: {
  message: string;
  esperado: string[];
  recibido: string[];
  onReintentar: () => void;
}) {
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-3 text-amber-900">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <div className="min-w-0">
          <p className="text-sm font-semibold">El archivo no tiene el formato esperado</p>
          <p className="mt-1 text-xs leading-relaxed">{message}</p>
          <div className="mt-2 grid gap-2 text-[11px] sm:grid-cols-2">
            <div>
              <div className="font-semibold uppercase tracking-wide">Esperado</div>
              <div className="mt-0.5 break-words opacity-90">{esperado.join(' · ')}</div>
            </div>
            <div>
              <div className="font-semibold uppercase tracking-wide">Recibido</div>
              <div className="mt-0.5 break-words opacity-90">
                {recibido.length > 0 ? recibido.join(' · ') : '(sin encabezado)'}
              </div>
            </div>
          </div>
          <Button type="button" variant="outline" size="sm" className="mt-3" onClick={onReintentar}>
            Elegir otro archivo
          </Button>
        </div>
      </div>
    </div>
  );
}

function Resumen({
  resumen,
  descartadas,
}: {
  resumen: {
    filasLeidas: number;
    conCambios: number;
    conProblema: number;
    sinCambios: number;
    noEncontrados: number;
  };
  descartadas: number;
}) {
  return (
    <div className="flex flex-wrap gap-2 text-xs">
      <Chip tone="emerald" label="A aplicar" value={resumen.conCambios} />
      {resumen.conProblema > 0 && (
        <Chip tone="rose" label="Con problema" value={resumen.conProblema} />
      )}
      <Chip tone="slate" label="Sin cambios" value={resumen.sinCambios} />
      {descartadas > 0 && <Chip tone="slate" label="Descartadas" value={descartadas} />}
      {/* Lo pedido explícitamente: las filas que no casan se descartan, pero
          se dice cuántas fueron — si no, un Excel mal armado se aplica "bien"
          a la mitad y nadie se entera. */}
      {resumen.noEncontrados > 0 && (
        <Chip tone="amber" label="Ignoradas (no están en este cierre)" value={resumen.noEncontrados} />
      )}
    </div>
  );
}

function Chip({
  tone,
  label,
  value,
}: {
  tone: 'emerald' | 'rose' | 'slate' | 'amber';
  label: string;
  value: number;
}) {
  const cls =
    tone === 'emerald'
      ? 'bg-emerald-100 text-emerald-800'
      : tone === 'rose'
        ? 'bg-rose-100 text-rose-800'
        : tone === 'amber'
          ? 'bg-amber-100 text-amber-800'
          : 'bg-secondary text-foreground';
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-md px-2 py-1', cls)}>
      <span className="font-semibold tabular-nums">{value}</span>
      <span>{label}</span>
    </span>
  );
}
